// src/components/CensusDataDisplay.jsx
import React, { useState, useEffect, useCallback } from 'react';
import TREE_SPECIES_OPTIONS from '../components/utils/TreeSpaciesOptions';
import PdfUploadField from './utils/PdfUploadField';

/* costanti di supporto */
const HEALTH_STATUS_OPTIONS = [
  { value: 'ottimo',   label: 'Ottimo'   },
  { value: 'buono',    label: 'Buono'    },
  { value: 'discreto', label: 'Discreto' },
  { value: 'cattivo',  label: 'Cattivo'  }
];

export default function CensusDataDisplay({
  censusData,        
  onChange,          
  onRemoveFile,      
  readOnly = false
}) {
  /* stato locale del form */
  const [form, setForm] = useState({
    specie:        '',
    altezza:       '',
    diametro:      '',
    stato_salute:  '',
    schedaPdf:     null,  
    documentoPdf:  null
  });

  /* ───────── sync con censusData ───────── */
  useEffect(() => {
    if (!censusData) return;
    setForm({
      specie:        censusData.specie        ?? '',
      altezza:       censusData.altezza       ?? '',
      diametro:      censusData.diametro      ?? '',
      stato_salute:  censusData.stato_salute  ?? '',
      schedaPdf:     censusData.scheda_pdf    ?? censusData.schedaPdf    ?? null,
      documentoPdf:  censusData.documento_pdf ?? censusData.documentoPdf ?? null
    });
  }, [censusData]);

  const propagate = useCallback(
    (partial) => {
      if (readOnly || !onChange) return;
      onChange({ ...censusData, ...form, ...partial });
    },
    [readOnly, onChange, censusData, form]
  );

  /* generatore di handler */
  const handleField = (field) => (arg) => {
    const value = arg?.target ? arg.target.value : arg;
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      propagate({ [field]: value });
      return next;
    });
  };

  const removeFileOptimistic = async (field) => {
    // 1. ui immediata
    handleField(field)(null);
    // 2. server
    try { await onRemoveFile?.(field); } catch (e) { console.error(e); }
  };

  const labelCls = 'block font-medium text-green-500 mb-2';
  const inputCls = 'w-full bg-primary text-white py-2 px-3 rounded border border-secondary min-h-[44px]';
  const valueCls = 'bg-secondary text-white py-2 px-3 rounded min-h-[44px] flex items-center';

  return (
    <div className="bg-primary rounded-xl p-4 mt-4 text-white">
      <h3 className="font-semibold text-md mb-4 text-green-500">Dati Censimento</h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* SPECIE */}
        <div>
          <label className={labelCls}>Specie</label>
          {readOnly ? (
            <div className={valueCls}>
              {TREE_SPECIES_OPTIONS.find((o) => o.value === form.specie)?.label || form.specie}
            </div>
          ) : (
            <select value={form.specie} onChange={handleField('specie')} className={inputCls}>
              <option value="" disabled>Seleziona specie…</option>
              {TREE_SPECIES_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          )}
        </div>

        {/* STATO SALUTE */}
        <div>
          <label className={labelCls}>Stato di salute</label>
          {readOnly ? (
            <div className={valueCls}>
              {HEALTH_STATUS_OPTIONS.find((o) => o.value === form.stato_salute)?.label || form.stato_salute}
            </div>
          ) : (
            <select value={form.stato_salute} onChange={handleField('stato_salute')} className={inputCls}>
              <option value="" disabled>Seleziona stato…</option>
              {HEALTH_STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          )}
        </div>

        {/* ALTEZZA */}
        <div>
          <label className={labelCls}>Altezza&nbsp;(m)</label>
          {readOnly ? (
            <div className={valueCls}>{form.altezza}</div>
          ) : (
            <input type="number" min="0" step="0.1" value={form.altezza} onChange={handleField('altezza')} className={inputCls} />
          )}
        </div>

        {/* DIAMETRO */}
        <div>
          <label className={labelCls}>Diametro&nbsp;(cm)</label>
          {readOnly ? (
            <div className={valueCls}>{form.diametro}</div>
          ) : (
            <input type="number" min="0" step="0.1" value={form.diametro} onChange={handleField('diametro')} className={inputCls} />
          )}
        </div>
      </div>

      {/* ─────────── upload PDF ─────────── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
        <PdfUploadField
          label="Scheda PDF"
          file={form.schedaPdf}
          onChange={handleField('schedaPdf')}
          onRemove={() => removeFileOptimistic('schedaPdf')}
          disabled={readOnly}
        />
        <PdfUploadField
          label="Documento PDF"
          file={form.documentoPdf}
          onChange={handleField('documentoPdf')}
          onRemove={() => removeFileOptimistic('documentoPdf')}
          disabled={readOnly}
        />
      </div>
    </div>
  );
}
